import { useState, useEffect } from "react";
import { Dialog, Stack, Chip, InputBase, IconButton, Button, Collapse, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useCreate, useNotify, useRefresh, useGetList, useGetIdentity } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { DateYMDString, ObjectType, ResourceName, TaskStatus, User, UserStatus, type TaskTemplate } from "@/types";
import { addDays, templateAppliesTo } from "../../utils/taskUtils";

// Shared task components
import { AssigneeSelector } from "../shared/AssigneeSelector";
import { DueDateSelector } from "../shared/DueDateSelector";
import { RecordSelector } from "../shared/RecordSelector";
import { NotesChip } from "../shared/NotesChip";
import { getSuggestionChipStyles } from "../shared/taskChipStyles";

interface TaskCreateDialogProps {
  open: boolean;
  onClose: () => void;
  defaultRecord?: { objectType: ObjectType; objectId: string; label?: string };
}

export const TaskCreateDialog = ({ open, onClose, defaultRecord }: TaskCreateDialogProps) => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [create, { isLoading }] = useCreate();
  const { data: currentUser } = useGetIdentity();
  const currentUserId = currentUser?.id ? String(currentUser.id) : null;

  const { data: activeUsers = [], isLoading: isLoadingUsers } = useGetList<User>(ResourceName.USERS, {
    pagination: { page: 1, perPage: 100 },
    filter: { status: UserStatus.ACTIVE },
  });

  // Form state
  const [objectType, setObjectType] = useState<ObjectType | null>(defaultRecord?.objectType ?? null);
  const [objectId, setObjectId] = useState<string | null>(defaultRecord?.objectId ?? null);
  const [recordLabel, setRecordLabel] = useState<string | undefined>(defaultRecord?.label);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [showDescription, setShowDescription] = useState(false);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<DateYMDString | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);

  // Default assignee to current user only if they are provisioned in this tenant.
  // Same guard as TaskComposerInline — prevents a 404 → retry storm (Bug #5158).
  useEffect(() => {
    if (isLoadingUsers || !currentUserId || assigneeId) return;
    if (activeUsers.some((u) => String(u.id) === currentUserId)) {
      setAssigneeId(currentUserId);
    }
  }, [currentUserId, activeUsers, isLoadingUsers]);

  // Data fetching. The API ignores applicable_object_types, so fetch every active
  // template (1000 = the API's MaxPageSize) and filter client-side (#7655).
  const { data: allTemplates = [] } = useGetList<TaskTemplate>(
    "task-templates",
    {
      pagination: { page: 1, perPage: 1000 },
      filter: { is_active: true, applicable_object_types: objectType },
    },
    { enabled: !!objectType && !!objectId }
  );
  const templates = allTemplates.filter((t) => templateAppliesTo(t, objectType));

  // Derived values
  const canSubmit = !!objectType && !!objectId && !!title.trim();

  // Handle record selection change
  const handleRecordChange = (newObjectType: ObjectType | null, newObjectId: string | null, newLabel?: string) => {
    // Clear template if type changes
    if (newObjectType !== objectType) {
      setTemplateId(null);
    }
    setObjectType(newObjectType);
    setObjectId(newObjectId);
    setRecordLabel(newLabel);
  };

  // Apply template
  const handleTemplateClick = (t: TaskTemplate) => {
    setTemplateId(t.id);
    setTitle(t.title);
    if (t.description) {
      setDescription(t.description);
      setShowDescription(true);
    }
    if (t.default_due_days) {
      setDueDate(addDays(t.default_due_days));
    }
  };

  // Submit
  const handleSubmit = async () => {
    if (!canSubmit) return;

    const data = {
      title: title.trim(),
      description: description.trim() || undefined,
      status: TaskStatus.OPEN,
      completed: false,
      display_claim_exhibit: true,
      record: {
        object_type: objectType,
        object_id: objectId,
        label: recordLabel,
      },
      ...(assigneeId && { assigned_user_id_ref: assigneeId }),
      ...(dueDate && { due_date: dueDate }),
      ...(templateId && { template_id_ref: templateId }),
    };

    await create(
      ResourceName.TASKS,
      { data },
      {
        onSuccess: () => {
          notify("Task created", { type: "success" });
          refresh();
          handleClose();
        },
        onError: (error: unknown) => {
          notifyError(error);
        },
      }
    );
  };

  // Reset and close
  const handleClose = () => {
    setObjectType(defaultRecord?.objectType ?? null);
    setObjectId(defaultRecord?.objectId ?? null);
    setRecordLabel(defaultRecord?.label);
    setTitle("");
    setDescription("");
    setShowDescription(false);
    setAssigneeId(currentUserId && activeUsers.some((u) => String(u.id) === currentUserId) ? currentUserId : null);
    setDueDate(null);
    setTemplateId(null);
    onClose();
  };

  // Keyboard handling
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSubmit) {
      e.preventDefault();
      void handleSubmit();
    }
  };
  const suggestionChip = getSuggestionChipStyles();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      onKeyDown={handleKeyDown}
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Stack sx={{ p: 2.5 }} spacing={2}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <RecordSelector
            objectType={objectType}
            objectId={objectId}
            label={recordLabel}
            editable
            onChange={handleRecordChange}
          />
          <IconButton size="small" onClick={handleClose} sx={{ color: "text.secondary" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Quick add suggestions (templates) */}
        <Collapse in={templates.length > 0 && !title}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Quick add
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {templates.slice(0, 4).map((t) => (
                <Chip
                  key={t.id}
                  label={t.title}
                  onClick={() => handleTemplateClick(t)}
                  sx={suggestionChip}
                  size="small"
                />
              ))}
            </Stack>
          </Stack>
        </Collapse>

        {/* Title */}
        <InputBase
          placeholder="What needs to be done?"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (templateId) setTemplateId(null);
          }}
          sx={{
            fontSize: 18,
            fontWeight: 500,
            "& input::placeholder": { color: "text.disabled", opacity: 1 },
          }}
          autoFocus
        />

        {/* Description */}
        <Collapse in={showDescription}>
          <TextField
            placeholder="Add notes..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
            size="small"
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Collapse>

        {/* Chips - using shared components */}
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <AssigneeSelector value={assigneeId} onChange={setAssigneeId} />

          <DueDateSelector value={dueDate} onChange={setDueDate} disablePast />

          {!showDescription && <NotesChip onClick={() => setShowDescription(true)} />}
        </Stack>

        {/* Footer */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.disabled">
            ⌘+Enter to create
          </Typography>
          <Button variant="contained" size="small" onClick={handleSubmit} disabled={!canSubmit || isLoading}>
            {isLoading ? "Creating..." : "Create task"}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};
